<script setup>
import DeleteApikomContentAction from "@frontJs/components/actions/projectManagement/seo/DeleteApikomContentAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import AlertBox from "@js/components/AlertBox.vue";
import ComboboxField from "@js/components/fields/ComboboxField.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";

const { t } = useI18n();

const types = [
  {
    value: "premium",
    label: t("label.premium"),
  },
  {
    value: "standard",
    label: t("label.standard"),
  },
];

const groups = [
  {
    value: "other",
    label: t("label.other"),
  },
  {
    value: "product",
    label: t("label.product"),
  },
  {
    value: "category",
    label: t("label.category"),
  },
];

const { tenant, websiteVariants, seoApikomContent } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
  websiteVariants: {
    type: Array,
    required: true,
  },
  seoApikomContent: {
    type: Object,
    required: false,
  },
});

const { route } = useRoute();

const form = reactive({
  websiteVariant: websiteVariants.find(
    ({ id }) => id === seoApikomContent?.websiteVariantId,
  ),
  group: seoApikomContent
    ? seoApikomContent.cmsCategoryId
      ? groups[2]
      : seoApikomContent.cmsProductId
        ? groups[1]
        : groups[0]
    : groups[0],
  category:
    seoApikomContent && seoApikomContent.eCommerceCategory
      ? {
          cmsCategoryId: seoApikomContent.eCommerceCategory.cmsCategoryId,
          languageName: seoApikomContent.eCommerceCategory.languageName,
          languageUrl: seoApikomContent.eCommerceCategory.languageUrl,
          seoContentProduct: seoApikomContent.eCommerceCategory
            .seoContentCategory
            ? {
                contentType: {
                  value:
                    seoApikomContent.eCommerceCategory.seoContentCategory
                      .contentType.value,
                  label:
                    seoApikomContent.eCommerceCategory.seoContentCategory
                      .contentType.label,
                },
              }
            : null,
        }
      : null,
  product:
    seoApikomContent && seoApikomContent.eCommerceProduct
      ? {
          cmsProductId: seoApikomContent.eCommerceProduct.cmsProductId,
          sku: seoApikomContent.eCommerceProduct.sku,
          languageName: seoApikomContent.eCommerceProduct.languageName,
          languageUrl: seoApikomContent.eCommerceProduct.languageUrl,
          seoContentProduct: seoApikomContent.eCommerceProduct.seoContentProduct
            ? {
                contentType: {
                  value:
                    seoApikomContent.eCommerceProduct.seoContentProduct
                      .contentType.value,
                  label:
                    seoApikomContent.eCommerceProduct.seoContentProduct
                      .contentType.label,
                },
              }
            : null,
        }
      : null,
  url: seoApikomContent ? seoApikomContent.url : "",
  type: seoApikomContent
    ? types.find(({ value }) => value === seoApikomContent.type.value)
    : types[0],
  date: seoApikomContent ? seoApikomContent.date : "",
});

const validationSchema = object({
  websiteVariant: object().required(t("error.required")),
  group: object()
    .shape({
      value: string().required(t("error.required")),
      label: string().required(t("error.required")),
    })
    .required(t("error.required")),
  category: object().when("group", {
    is: (value) => value.value === "category",
    then: () => object().required(t("error.required")),
    otherwise: () => object().nullable(),
  }),
  product: object().when("group", {
    is: (value) => value.value === "product",
    then: () => object().required(t("error.required")),
    otherwise: () => object().nullable(),
  }),
  url: string().required(t("error.required")),
  type: object()
    .shape({
      value: string().required(t("error.required")),
      label: string().required(t("error.required")),
    })
    .required(t("error.required")),
  date: string().nullable(),
});

const isSubmitting = ref(false);

const selectedTab = ref(0);

const categories = ref([]);

const products = ref([]);

function changeTab(index) {
  selectedTab.value = index;
}

function submit() {
  fetch(
    seoApikomContent
      ? route("front.projectManagement.seo.postApikomContent", {
          tenant: tenant.id,
          id: seoApikomContent.id,
        })
      : route("front.projectManagement.seo.postApikomContent", {
          tenant: tenant.id,
        }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        websiteVariantId: form.websiteVariant.id,
        cmsCategoryId: form.category ? form.category.cmsCategoryId : null,
        cmsProductId: form.product ? form.product.cmsProductId : null,
        url: form.url,
        type: form.type.value,
        date: form.date,
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then(() => {
      toast.success(t("toast.successfullySaved"), {
        position: "top-right",
      });

      goBack();
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(
        route("front.projectManagement.seo.apikomContents", {
          tenant: tenant.id,
        }),
      );
    });
}

function deleteCallback() {
  toast.success(t("all.toast.successfullyDeleted"), { position: "top-right" });

  router.visit(
    route("front.projectManagement.seo.apikomContents", {
      tenant: tenant.id,
    }),
  );
}

function updateCategories(event) {
  fetch(
    route("front.projectManagement.seo.postApikomContentCategories", {
      tenant: tenant.id,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        search: event.target.value,
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then((data) => {
      categories.value = data;
    })
    .catch((error) => {
      console.error(error);
    });
}

function updateProducts(event) {
  fetch(
    route("front.projectManagement.seo.postApikomContentProducts", {
      tenant: tenant.id,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        search: event.target.value,
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then((data) => {
      products.value = data;
    })
    .catch((error) => {
      console.error(error);
    });
}

function displayCategory(category) {
  return category
    ? t("label.id") +
        " : " +
        category.cmsCategoryId +
        " - " +
        t("label.name") +
        " : " +
        category.languageName
    : "";
}

function displayProduct(product) {
  return product
    ? t("label.id") +
        " : " +
        product.cmsProductId +
        " - " +
        t("label.sku") +
        " : " +
        product.sku +
        " - " +
        t("label.name") +
        " : " +
        product.languageName
    : "";
}

watch(
  () => form.group,
  () => {
    form.category = null;
    form.product = null;
    form.url = "";
  },
);

watch(
  () => form.category,
  () => {
    if (
      form.group.value === "category" &&
      form.category &&
      form.category.languageUrl
    ) {
      form.url = form.category.languageUrl;
    }
  },
);

watch(
  () => form.product,
  () => {
    if (
      form.group.value === "product" &&
      form.product &&
      form.product.languageUrl
    ) {
      form.url = form.product.languageUrl;
    }
  },
);
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    @submit="submit"
    v-slot="{ errors }"
  >
    <AlertBox
      v-if="
        (form.group.value === 'product' &&
          form.product &&
          form.product.seoContentProduct &&
          form.product.seoContentProduct.contentType.value !==
            form.type.value) ||
        (form.group.value === 'category' &&
          form.category &&
          form.category.seoContentCategory &&
          form.category.seoContentCategory.contentType.value !==
            form.type.value)
      "
      type="danger"
    >
      {{ t("text.contentTypeAreNotTheSame") }}
    </AlertBox>

    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
      }"
    >
      <template #panels>
        <TabPanel>
          <ComboboxField
            v-model="form.websiteVariant"
            field-name="websiteVariant"
            field-id="websiteVariant"
            :label="t('label.websiteVariant')"
            filtered-option-key="name"
            :options="websiteVariants"
            required
          >
            <template #optionContent="{ option }">
              {{ option.name }}
            </template>
          </ComboboxField>

          <ListboxField
            v-model="form.group"
            field-id="group"
            field-name="group"
            :label="t('label.group')"
            :options="groups"
            by="value"
            required
          >
            <template #buttonContent>
              {{ form.group.label }}
            </template>

            <template #optionContent="{ option }">
              {{ option.label }}
            </template>
          </ListboxField>

          <ComboboxField
            v-if="form.group.value === 'category'"
            v-model="form.category"
            field-name="category"
            field-id="category"
            :label="t('label.category')"
            filtered-option-key="cmsCategoryId"
            :multiple-filtered-option-keys="[
              'cmsCategoryId',
              'languageName',
              'languageUrl',
            ]"
            :options="categories"
            :display-value="displayCategory(form.category)"
            @keyup="updateCategories"
            required
          >
            <template #optionContent="{ option }">
              {{ displayCategory(option) }}
            </template>
          </ComboboxField>

          <ComboboxField
            v-if="form.group.value === 'product'"
            v-model="form.product"
            field-name="product"
            field-id="product"
            :label="t('label.product')"
            filtered-option-key="cmsProductId"
            :multiple-filtered-option-keys="[
              'cmsProductId',
              'sku',
              'languageName',
              'languageUrl',
            ]"
            :options="products"
            :display-value="displayProduct(form.product)"
            @keyup="updateProducts"
            required
          >
            <template #optionContent="{ option }">
              {{ displayProduct(option) }}
            </template>
          </ComboboxField>

          <InputField
            v-model="form.url"
            :label="t('label.url')"
            field-name="url"
            field-id="url"
            type="url"
            required
            :disabled="
              form.group.value === 'product' || form.group.value === 'category'
            "
          />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <ListboxField
              v-model="form.type"
              field-id="type"
              field-name="type"
              :label="t('label.type')"
              :options="types"
              by="value"
              required
            >
              <template #buttonContent>
                {{ form.type.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>

            <InputField
              v-model="form.date"
              :label="t('label.date')"
              field-name="date"
              field-id="date"
              type="month"
            />
          </div>
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteApikomContentAction
        v-if="seoApikomContent"
        :tenant="tenant"
        :seo-apikom-content="seoApikomContent"
        :callback="deleteCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteApikomContentAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>
    </div>
  </Form>
</template>
